import React, { useMemo } from 'react';
import { Box, Card, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks/hooks';
import MostLikedToursContainer from '../Discover/MostLikedToursContainer';
import OngoingToursContainer from '../Discover/OngoingToursContainer';

const getCommunityNamesFromTours = (owners: Array<string | undefined>): string[] => {
    return Array.from(new Set(owners.filter((owner): owner is string => Boolean(owner && owner.trim()))));
};

const getOwnerScore = (ongoingOwners: Array<string | undefined>, mostLikedOwners: Array<string | undefined>) => {
    const score = new Map<string, number>();

    ongoingOwners.forEach((owner) => {
        if (!owner) return;
        score.set(owner, (score.get(owner) ?? 0) + 2);
    });

    mostLikedOwners.forEach((owner) => {
        if (!owner) return;
        score.set(owner, (score.get(owner) ?? 0) + 1);
    });

    return score;
};

const getRandomItems = (items: string[], limit: number): string[] => {
    const copied = [...items];
    for (let i = copied.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copied[i], copied[j]] = [copied[j], copied[i]];
    }
    return copied.slice(0, limit);
};

const VanliferChipList = ({ items, loading, emptyText }: { items: string[]; loading: boolean; emptyText: string }) => {
    if (loading) {
        return (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} variant="rounded" width={140} height={32} />
                ))}
            </Stack>
        );
    }

    if (!items.length) {
        return <Typography color="text.secondary">{emptyText}</Typography>;
    }

    return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {items.map((communityName) => {
                if (communityName === 'Gelöschter Vanlifer') {
                    return <Chip key={communityName} label={communityName} variant="outlined" />;
                }

                return (
                    <Chip
                        key={communityName}
                        label={communityName}
                        component={Link}
                        clickable
                        to={`/vanlifer/Community/Profile/${communityName}`}
                        variant="outlined"
                        sx={{ textDecoration: 'none' }}
                    />
                );
            })}
        </Stack>
    );
};

const SectionCard = ({
    overheader,
    title,
    children,
}: {
    overheader: string;
    title: string;
    children: React.ReactNode;
}) => (
    <Card sx={{ mb: 2 }}>
        <CardContent>
            <Typography variant="overline" color="text.secondary">
                {overheader}
            </Typography>

            <Typography
                variant="h6"
                sx={{
                    fontFamily: 'bubblegum-sans',
                    fontWeight: 400,
                    lineHeight: 1.55,
                    color: 'text.primary',
                    mb: 2,
                }}
            >
                "{title}"
            </Typography>
            {children}
        </CardContent>
    </Card>
);

export default function DiscoverTab() {
    const ongoingTours = useAppSelector((state) => state.discoverTours.ongoingTours);
    const mostLikedTours = useAppSelector((state) => state.discoverTours.mostLikedTours);
    const loading = useAppSelector((state) => state.discoverTours.loading);
    const error = useAppSelector((state) => state.discoverTours.error);

    const ongoingOwners = useMemo(() => (ongoingTours ?? []).map((tour) => tour.owner), [ongoingTours]);
    const mostLikedOwners = useMemo(() => (mostLikedTours ?? []).map((tour) => tour.owner), [mostLikedTours]);

    const topActiveVanlifers = useMemo(() => {
        const score = getOwnerScore(ongoingOwners, mostLikedOwners);
        return Array.from(score.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([communityName]) => communityName)
            .slice(0, 8);
    }, [ongoingOwners, mostLikedOwners]);

    const randomVanlifers = useMemo(() => {
        const uniqueNames = getCommunityNamesFromTours([...ongoingOwners, ...mostLikedOwners]);
        return getRandomItems(uniqueNames, 8);
    }, [ongoingOwners, mostLikedOwners]);

    if (error) {
        return <Typography color="error">Fehler: {error}</Typography>;
    }

    return (
        <Box>
            <Box mb={5}>
                <SectionCard overheader="Entdecken" title="Die aktivsten Vanlifer">
                    <VanliferChipList
                        items={topActiveVanlifers}
                        loading={loading && !topActiveVanlifers.length}
                        emptyText="Derzeit keine aktiven Vanlifer gefunden."
                    />
                </SectionCard>
            </Box>

            <Box mb={5}>
                <SectionCard overheader="Entdecken" title="Reisen aus der Community">
                    <Box mb={5}>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Beliebte Reisen
                        </Typography>
                        <MostLikedToursContainer />
                    </Box>

                    <Typography variant="h5" sx={{ mb: 1 }}>
                        Aktuell unterwegs
                    </Typography>
                    <OngoingToursContainer />
                </SectionCard>
            </Box>

            <Box>
                <SectionCard overheader="Entdecken" title="Zufällige Vanlifer">
                    <VanliferChipList
                        items={randomVanlifers}
                        loading={loading && !randomVanlifers.length}
                        emptyText="Derzeit keine Vanlifer gefunden."
                    />
                </SectionCard>
            </Box>
        </Box>
    );
}
